/**
 * Whether a panel tab shows the provider's live view (Browserbase): only the
 * agent's tab, which is what the live view shows, and never while picking
 * (the picker needs our own frames). The person's own tabs (AMO-232) draw
 * their own frames. An unknown agent tab (an older server) is the one tab.
 */
export function showsProviderLiveView(input: {
  readonly liveViewUrl: string | null;
  readonly tabId: string | null;
  readonly agentTab: string | null;
  readonly picking: boolean;
}): boolean {
  if (input.liveViewUrl === null || input.picking) return false;
  return input.tabId === null || input.agentTab === null || input.tabId === input.agentTab;
}

/** Where an object-contain frame is drawn in its element: offset (element px) and scale. */
export interface DrawnFrame {
  readonly left: number;
  readonly top: number;
  readonly scale: number;
}

/** The frame is centred, with empty bands on the two sides its shape leaves. */
export function drawnFrame(
  element: { readonly width: number; readonly height: number },
  frame: { readonly width: number; readonly height: number },
): DrawnFrame {
  const scale = Math.min(element.width / frame.width, element.height / frame.height);
  return {
    left: (element.width - frame.width * scale) / 2,
    top: (element.height - frame.height * scale) / 2,
    scale,
  };
}

/**
 * Map a pointer position on the screencast `<img>` to the page's CSS pixels.
 *
 * The image is drawn with `object-contain`, so a frame whose shape differs from
 * the element's sits inside it with empty bands on two sides. Positions are
 * measured against the drawn frame, not the element, and a position in a band
 * is not on the page at all (null).
 */
export function pagePointFromClient(
  pointer: { readonly clientX: number; readonly clientY: number },
  element: { readonly left: number; readonly top: number; readonly width: number; readonly height: number },
  frame: { readonly width: number; readonly height: number },
): { x: number; y: number } | null {
  if (element.width <= 0 || element.height <= 0 || frame.width <= 0 || frame.height <= 0) return null;
  const drawn = drawnFrame(element, frame);
  const x = (pointer.clientX - element.left - drawn.left) / drawn.scale;
  const y = (pointer.clientY - element.top - drawn.top) / drawn.scale;
  if (x < 0 || y < 0 || x >= frame.width || y >= frame.height) return null;
  return { x: Math.round(x), y: Math.round(y) };
}

/** What a key event on the stage does to the remote browser ("take control", AMO-179). */
export type RemoteKeyAction =
  | { readonly kind: "key"; readonly key: string }
  /** The page's selection, onto the person's own clipboard. */
  | { readonly kind: "copy" }
  /** Copy, then delete the selection. */
  | { readonly kind: "cut" }
  /** Left to the browser: its `paste` event carries the text. */
  | { readonly kind: "paste" }
  /** Kept by the person's own browser (closing or opening tabs and windows). */
  | { readonly kind: "local" }
  /** Nothing to send: composition in progress, dead keys, the OS key. */
  | { readonly kind: "ignore" };

const ASCII_LETTER = /^[A-Za-z]$/;

/** The Latin letter at a key's physical position (`KeyC` → `c`), or null. */
function physicalLetter(code: string | undefined): string | null {
  return code !== undefined && /^Key[A-Z]$/.test(code) ? code.slice(3).toLowerCase() : null;
}

/** Shortcuts the person's own browser keeps; the page never sees them. */
const LOCAL_SHORTCUT_KEYS = new Set(["w", "t", "n", "q", "tab"]);
const PLACEHOLDER_KEYS = new Set(["Dead", "Process", "Unidentified", "Compose", "OS"]);

/**
 * Map a key event to the remote browser. The remote is Linux Chromium, so a
 * Mac's Command plays as Control (Cmd+A selects all there too); copy, cut and
 * paste go through the person's own clipboard rather than the remote's.
 */
export function remoteKeyAction(
  event: {
    readonly key: string;
    /** The physical key (`KeyC`): the same on every layout. */
    readonly code?: string;
    readonly ctrlKey: boolean;
    readonly metaKey: boolean;
    readonly altKey: boolean;
    readonly isComposing?: boolean;
    readonly keyCode?: number;
  },
  isMac: boolean,
): RemoteKeyAction {
  if (event.isComposing || event.keyCode === 229 || PLACEHOLDER_KEYS.has(event.key)) return { kind: "ignore" };
  if (event.key === "Meta") return isMac ? { kind: "key", key: "Control" } : { kind: "ignore" };
  // A shortcut is a physical key: on a Hebrew or Cyrillic layout Ctrl+C
  // reports a non-Latin `key`, but its `code` is still KeyC.
  const shortcut = (event.ctrlKey || (isMac && event.metaKey)) && !event.altKey;
  const letter = shortcut && !ASCII_LETTER.test(event.key) ? physicalLetter(event.code) : null;
  const command = isMac ? event.metaKey : event.ctrlKey;
  if (command && !event.altKey) {
    const key = letter ?? event.key.toLowerCase();
    if (LOCAL_SHORTCUT_KEYS.has(key)) return { kind: "local" };
    if (key === "c") return { kind: "copy" };
    if (key === "x") return { kind: "cut" };
    if (key === "v") return { kind: "paste" };
  }
  // Ctrl+A, Ctrl+Z and the like reach the page by their Latin letter, as the
  // remote browser's shortcuts are bound; never as the layout's character.
  return { kind: "key", key: letter ?? event.key };
}

/** The mouse button a pointer event names, as the remote browser knows it. */
export function remoteButton(button: number): "left" | "middle" | "right" | null {
  return button === 0 ? "left" : button === 1 ? "middle" : button === 2 ? "right" : null;
}

/** A press soon enough after the last, close enough to it, counts as its next click. */
const MULTI_CLICK_MS = 500;
const MULTI_CLICK_DISTANCE = 5;

export interface PressRecord {
  readonly at: number;
  readonly x: number;
  readonly y: number;
  readonly button: string;
  readonly count: number;
}

/**
 * The click count of a new press (1 single, 2 double, 3 triple: select word,
 * select paragraph). Pointer events carry none, so the stage counts them.
 */
export function nextPress(
  previous: PressRecord | null,
  press: { readonly at: number; readonly x: number; readonly y: number; readonly button: string },
): PressRecord {
  const repeated =
    previous !== null &&
    previous.button === press.button &&
    press.at - previous.at <= MULTI_CLICK_MS &&
    Math.abs(press.x - previous.x) <= MULTI_CLICK_DISTANCE &&
    Math.abs(press.y - previous.y) <= MULTI_CLICK_DISTANCE;
  return { ...press, count: repeated ? Math.min(3, previous.count + 1) : 1 };
}

/**
 * Ctrl+wheel (and a trackpad pinch, which browsers send as one) over the
 * browser zooms the browser, not the Amoreg page around it (AMO-233). A
 * mouse notch is about 100 px, a pinch a few px a step: one zoom step per
 * this much, so neither races.
 */
export const WHEEL_ZOOM_STEP_PX = 50;

/**
 * Zoom steps a Ctrl+wheel brings (positive zooms in: wheel up), and what is
 * left towards the next. Lines (Firefox) count as 40 px, as for scrolling.
 */
export function wheelZoomSteps(
  carried: number,
  deltaY: number,
  deltaMode: number,
): { readonly steps: number; readonly carried: number } {
  const total = carried - deltaY * (deltaMode === 1 ? 40 : 1);
  const steps = Math.trunc(total / WHEEL_ZOOM_STEP_PX);
  return { steps, carried: total - steps * WHEEL_ZOOM_STEP_PX };
}

/** The browser's own zoom keys, with Ctrl (Command on a Mac): in, out, back to 100%. */
export function zoomShortcut(event: {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
}): "in" | "out" | "reset" | null {
  if (!(event.ctrlKey || event.metaKey) || event.altKey) return null;
  switch (event.key) {
    case "+":
    case "=":
      return "in";
    case "-":
    case "_":
      return "out";
    case "0":
      return "reset";
    default:
      return null;
  }
}

/**
 * How long after the person last showed use (a pointer over the browser, a
 * click into it) a provider's live view still counts as in use. Below the
 * server's five-minute idle timeout, so a view nobody touches lets its
 * browser sleep on schedule (AMO-230).
 */
export const LIVE_VIEW_ENGAGED_MS = 4 * 60_000;
/**
 * The live view's input never reaches this page: someone working in it shows
 * only as the keyboard focus sitting in it (their pointer moves inside it are
 * invisible here too). That counts this much longer after the last pointer
 * sign: long enough that a person working in the view is not put to sleep
 * under their hands, while a window left focused on it still sleeps in the
 * end (it wakes, logins kept).
 */
export const LIVE_VIEW_FOCUSED_MS = 60 * 60_000;

export interface LiveViewUse {
  /** The page's tab is showing. */
  readonly visible: boolean;
  /** The keyboard focus is in the live view, with this window focused. */
  readonly focusedInView: boolean;
  /** The last pointer sign on the view (enter, move, press), in millis. */
  readonly lastSignAt: number;
  readonly now: number;
}

/**
 * Whether an open live view still keeps its browser awake. Open and visible
 * alone is not use: a panel left open on a screen nobody watches must let the
 * browser sleep after the idle timeout.
 */
export function liveViewInUse(use: LiveViewUse): boolean {
  if (!use.visible) return false;
  const since = use.now - use.lastSignAt;
  return since < LIVE_VIEW_ENGAGED_MS || (use.focusedInView && since < LIVE_VIEW_FOCUSED_MS);
}

/**
 * Whether the panel wakes its chat's sleeping browser on its own, as when
 * coming back to the chat: shown, the browser asleep (idle, or its session
 * ended under it) with no session and not waiting in line for one, and not
 * `handled` by this panel yet (woken once, or seen running: a browser that
 * falls asleep under an open panel stays asleep, and a wake that fails
 * leaves the resume button, never a loop). A browser stopped on purpose is
 * not asleep, so it is never woken.
 */
export function shouldWakeDormantBrowser(input: {
  readonly visible: boolean;
  readonly available: boolean;
  readonly dormant: boolean;
  readonly liveSession: string | null;
  readonly waiting: number | null;
  readonly handled: boolean;
}): boolean {
  return (
    input.visible &&
    input.available &&
    input.dormant &&
    input.liveSession === null &&
    input.waiting === null &&
    !input.handled
  );
}

/** Why a copy wrote nothing: the page had no selection; the clipboard is left as it was. */
export const NOTHING_SELECTED = "nothing selected";

/** Text a clipboard write waits for: the page's answer to a copy, or its abandonment. */
export interface PendingClipboardText {
  readonly text: Promise<string>;
  readonly resolve: (text: string) => void;
  readonly reject: (reason: Error) => void;
  /** The page's selection was longer than the text copied (a cut must keep it). */
  truncated: boolean;
}

export function pendingClipboardText(): PendingClipboardText {
  let resolve!: (text: string) => void;
  let reject!: (reason: Error) => void;
  const text = new Promise<string>((onText, onError) => {
    resolve = onText;
    reject = onError;
  });
  // Settled by whoever waits on it; never an unhandled rejection on its own.
  text.catch(() => undefined);
  return { text, resolve, reject, truncated: false };
}

/** The slice of `navigator.clipboard` used here (tests pass a fake). */
export interface ClipboardLike<Item> {
  readonly write?: (items: Item[]) => Promise<void>;
  readonly writeText: (text: string) => Promise<void>;
}

/**
 * Write text that is not known yet. Safari allows a clipboard write only
 * inside the key press that asked for it, and the page's selection arrives
 * a round trip later: so the write starts now, with a `ClipboardItem` whose
 * text is a promise. Where that is not available, or is refused, the text is
 * written once it arrives (Chrome and Firefox allow that). Rejects when the
 * text is abandoned (nothing selected, no answer) or the write fails.
 */
export function writeClipboardWhenReady<Item>(
  text: Promise<string>,
  clipboard: ClipboardLike<Item>,
  makeItem: ((data: Record<string, Promise<Blob>>) => Item) | null,
): Promise<void> {
  const writeLater = () => text.then((value) => clipboard.writeText(value));
  if (!makeItem || !clipboard.write) return writeLater();
  const blob = text.then((value) => new Blob([value], { type: "text/plain" }));
  // Whoever writes settles it; an abandoned copy is no unhandled rejection.
  blob.catch(() => undefined);
  let item: Item;
  try {
    item = makeItem({ "text/plain": blob });
  } catch {
    return writeLater();
  }
  return clipboard.write([item]).catch(writeLater);
}

/** How an input goes over the op route when the socket is not open; null when it cannot. */
export interface FallbackInput {
  readonly action: "type" | "press" | "click" | "scroll";
  readonly params: Readonly<Record<string, string>>;
}

type QueuedInput<M> =
  | { readonly kind: "input"; readonly message: M; readonly fallback: FallbackInput | null }
  /** A cut in progress: nothing after it is sent until `release`. */
  | { readonly kind: "hold" };

export interface InputSequencerOptions<M> {
  /** Send over the socket now; false when it is not open. */
  readonly send: (message: M) => boolean;
  /** Whether the socket is open (to batch typing while it is not). */
  readonly isOpen: () => boolean;
  /** Play over the op route; settles when the page has it. */
  readonly runFallback: (input: FallbackInput) => Promise<unknown>;
  /** An input that could go neither way (the socket closed, and it has no op). */
  readonly onDropped?: (message: M) => void;
}

/**
 * Inserted text travels in pieces of at most this many characters: an
 * op-route request carries it in its URL.
 */
export const INSERTED_TEXT_PIECE = 1_000;

/** The most text the server inserts at once (server lib/humanInput MAX_INSERTED_TEXT). */
export const MAX_INSERTED_TEXT = 20_000;

/** Where to cut `text` at or before `at` without splitting a character in two (a surrogate pair). */
const safeCut = (text: string, at: number): number => {
  if (at >= text.length || at <= 0) return Math.min(Math.max(at, 0), text.length);
  const before = text.charCodeAt(at - 1);
  return before >= 0xd800 && before <= 0xdbff ? at - 1 : at;
};

/**
 * Text to insert, as the pieces it travels in: cut to what the server inserts
 * at most (`cut` says whether anything was left out), then into pieces of at
 * most INSERTED_TEXT_PIECE. No cut splits a character (an emoji) in two.
 */
export const insertedTextPieces = (raw: string): { readonly pieces: string[]; readonly cut: boolean } => {
  const text = raw.slice(0, safeCut(raw, MAX_INSERTED_TEXT));
  const pieces: string[] = [];
  for (let start = 0; start < text.length; ) {
    const end = safeCut(text, start + INSERTED_TEXT_PIECE);
    // A piece never comes out empty: a lone pair at the edge goes whole.
    const stop = end > start ? end : Math.min(start + 2, text.length);
    pieces.push(text.slice(start, stop));
    start = stop;
  }
  return { pieces, cut: text.length < raw.length };
};

/**
 * Every input of the panel, in one order ("take control", AMO-179). Each goes
 * over the socket when it is open, else over the op route, and nothing is
 * sent while an op-route input before it is still in flight: a click can
 * never move the focus before earlier typing has landed, whichever way each
 * went, and an input queued when the socket closed takes the op route
 * instead of being lost. Typing that waits for the op route goes as one
 * request. A hold (a cut, waiting for its copy) stops everything after it
 * until `release`, which can put inputs (the cut's delete) first.
 */
export class InputSequencer<M> {
  private readonly queue: Array<QueuedInput<M>> = [];
  private running = false;
  private held = false;
  private cancelled = false;

  constructor(private readonly options: InputSequencerOptions<M>) {}

  submit(message: M, fallback: FallbackInput | null = null): void {
    if (this.cancelled) return;
    this.queue.push({ kind: "input", message, fallback });
    this.run();
  }

  /** Stop after everything submitted so far, until `release`. */
  hold(): void {
    if (this.cancelled) return;
    this.queue.push({ kind: "hold" });
    this.run();
  }

  /** End a hold: `first` goes before everything that waited behind it. */
  release(first: ReadonlyArray<{ readonly message: M; readonly fallback: FallbackInput | null }> = []): void {
    if (this.cancelled) return;
    if (!this.held) {
      // Released before the queue reached the hold: drop the hold itself.
      const index = this.queue.findIndex((item) => item.kind === "hold");
      if (index === -1) return;
      this.queue.splice(index, 1, ...first.map((input) => ({ kind: "input" as const, ...input })));
      this.run();
      return;
    }
    this.held = false;
    this.queue.unshift(...first.map((input) => ({ kind: "input" as const, ...input })));
    this.run();
  }

  /**
   * Drop everything waiting and send nothing more: the browser session it was
   * for has gone (another chat, or a new session). An op-route input already
   * in flight finishes on its own session; nothing follows it.
   */
  cancel(): void {
    this.cancelled = true;
    this.queue.length = 0;
    this.held = false;
  }

  /** Nothing waiting, held or in flight. */
  get idle(): boolean {
    return !this.running && !this.held && this.queue.length === 0;
  }

  private run(): void {
    if (this.running) return;
    this.running = true;
    void this.drain();
  }

  private async drain(): Promise<void> {
    try {
      while (!this.cancelled && !this.held && this.queue.length > 0) {
        const item = this.queue.shift()!;
        if (item.kind === "hold") {
          this.held = true;
          break;
        }
        // Over the socket: synchronous, in order, nothing to wait for.
        if (this.options.send(item.message)) continue;
        if (!item.fallback) {
          this.options.onDropped?.(item.message);
          continue;
        }
        let fallback = item.fallback;
        // Typing that waited for the op route goes as one request, until
        // anything else (or an open socket) comes next, and at most a piece
        // long: the text travels in the request's URL.
        if (fallback.action === "type") {
          let text = fallback.params.text ?? "";
          while (!this.options.isOpen()) {
            const next = this.queue[0];
            if (!next || next.kind !== "input") break;
            if (next.fallback === null) {
              this.queue.shift();
              this.options.onDropped?.(next.message);
              continue;
            }
            if (next.fallback.action !== "type") break;
            const more = next.fallback.params.text ?? "";
            if (text.length + more.length > INSERTED_TEXT_PIECE) break;
            this.queue.shift();
            text += more;
          }
          fallback = { action: "type", params: { text } };
        }
        await this.options.runFallback(fallback).catch(() => undefined);
      }
    } finally {
      this.running = false;
    }
    // Something arrived while the last op-route input was in flight.
    if (!this.cancelled && !this.held && this.queue.length > 0) this.run();
  }
}
